import React, {useCallback} from 'react'

import {useDragAndDrop} from '../../hooks/use-drag-and-drop'
import {useDragAndDropItem} from '../../hooks/use-drag-and-drop-item'

export interface MoveDialogTriggerProps<T> {
  /**
   * The clickable component that will trigger the move dialog to open
   */
  Component: T
  /**
   * Optional: The ref to return focus to after the move dialog is closed
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

/**
 * A trigger component that opens the move dialog when clicked.
 *
 * @param props MoveDialogTriggerProps
 */
export const MoveDialogTrigger = <T extends React.ElementType>({
  ref,
  Component,
  returnFocusRef: externalReturnFocusRef,
  ...props
}: MoveDialogTriggerProps<T> & React.ComponentProps<T> & {ref?: React.Ref<HTMLElement>}) => {
  const {title, index} = useDragAndDropItem()
  const {items, openMoveDialog} = useDragAndDrop()

  const openDialog = useCallback(() => {
    const returnFocusRef = (externalReturnFocusRef ?? ref) as React.RefObject<HTMLElement | null>
    openMoveDialog(title, index, returnFocusRef)
  }, [openMoveDialog, title, index, externalReturnFocusRef, ref])

  if (items.length === 1) return null

  return <Component {...props} onClick={openDialog} onSelect={openDialog} ref={ref} />
}

MoveDialogTrigger.displayName = 'MoveDialogTrigger'
